import type { CSSProperties, ReactNode } from 'react';

/**
 * Camera tag chip — one look for a tag wherever it appears (list, filter, form, Settings).
 *
 * A tag stores a palette KEY ('blue'), not a colour; this file owns what each key looks like.
 * Tinted with color-mix from one mid-tone per key so the chip reads on both themes — a Tailwind
 * `bg-x/15` modifier is not an option here, the theme tokens are bare var()s and v3 drops it.
 */
export const CAMERA_TAG_COLORS: Record<string, string> = {
  slate: '#64748b',
  blue: '#3b82f6',
  green: '#16a34a',
  amber: '#d97706',
  red: '#dc2626',
  purple: '#9333ea',
  teal: '#0d9488',
  pink: '#db2777',
};

export function cameraTagStyle(color?: string | null, selected = false): CSSProperties {
  const c = CAMERA_TAG_COLORS[color || ''] || CAMERA_TAG_COLORS.slate;
  return {
    color: c,
    backgroundColor: `color-mix(in srgb, ${c} ${selected ? 22 : 12}%, transparent)`,
    borderColor: `color-mix(in srgb, ${c} ${selected ? 70 : 30}%, transparent)`,
  };
}

export interface CameraTagChipProps {
  name: string;
  color?: string | null;
  /** Shown before the name, e.g. the category ("Department: Traffic") where context is lost. */
  prefix?: string;
  selected?: boolean;
  title?: string;
  className?: string;
  children?: ReactNode;
}

export default function CameraTagChip({ name, color, prefix, selected, title, className = '', children }: CameraTagChipProps) {
  return (
    <span
      title={title || (prefix ? `${prefix}: ${name}` : name)}
      className={`inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${className}`}
      style={cameraTagStyle(color, selected)}
    >
      {prefix && <span className="opacity-70">{prefix}:</span>}
      <span className="truncate">{name}</span>
      {children}
    </span>
  );
}
